/**
 * Cashfree Payment Integration
 * Backend URL: https://ago-backend-awou.onrender.com
 * Mode: production
 * Webhook URL: https://ago-backend-awou.onrender.com/api/payment/webhook
 */

const BACKEND_URL = 'https://ago-backend-awou.onrender.com';
const CASHFREE_MODE = 'production'; // live production mode

/**
 * Load Cashfree JS SDK dynamically
 */
const loadCashfreeSDK = () => {
  return new Promise((resolve, reject) => {
    if (window.Cashfree) {
      resolve(window.Cashfree);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    script.onload = () => {
      if (window.Cashfree) {
        resolve(window.Cashfree);
      } else {
        reject(new Error('Cashfree SDK failed to load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Cashfree SDK'));
    document.head.appendChild(script);
  });
};

/**
 * Create an order on the backend and get paymentSessionId
 * @param {object} orderData - { amount, customerName, customerEmail, customerPhone, orderNote }
 * @returns {string} paymentSessionId
 */
const createOrder = async (orderData) => {
  const response = await fetch(`${BACKEND_URL}/api/payment/create-order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      amount: parseFloat(orderData.amount),
      currency: 'INR',
      serviceType: orderData.serviceType || 'ots',
      serviceId: orderData.serviceId || 'ots-service',
      serviceDescription: orderData.orderNote || 'AGO Service Payment',
      customerName: orderData.customerName,
      customerEmail: orderData.customerEmail,
      customerPhone: orderData.customerPhone,
      otsSubmissionId: orderData.otsSubmissionId || `ots_${Date.now()}`,
      notes: orderData.notes || {},
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    console.error('❌ create-order backend error:', err);
    throw new Error(err.message || err.error || `Server error: ${response.status}`);
  }

  const data = await response.json();

  if (!data.data?.paymentSessionId) {
    console.error('❌ Unexpected response shape:', data);
    throw new Error('No payment session ID returned from server');
  }

  return data.data.paymentSessionId;
};

/**
 * Open Cashfree checkout
 * @param {object} paymentData - { amount, customerName, customerEmail, customerPhone, orderNote }
 * @param {function} onSuccess - called with { transactionId, orderId }
 * @param {function} onError   - called with { message }
 */
export const initiateCashfreePayment = async (paymentData, onSuccess, onError) => {
  try {
    // 1. Load SDK
    const CashfreeSDK = await loadCashfreeSDK();
    const cashfree = CashfreeSDK({ mode: CASHFREE_MODE });

    // 2. Get paymentSessionId from backend
    const paymentSessionId = await createOrder(paymentData);

    // 3. Open Cashfree checkout
    const checkoutOptions = {
      paymentSessionId,
      redirectTarget: '_modal', // opens as modal overlay
    };

    const result = await cashfree.checkout(checkoutOptions);

    // User closed/dismissed the modal without paying
    if (!result || (!result.error && !result.paymentDetails)) {
      onError({ message: 'cancelled' });
      return;
    }

    if (result.error) {
      const msg = result.error.message?.toLowerCase() || '';
      if (msg.includes('cancel') || msg.includes('close') || msg.includes('dismiss')) {
        onError({ message: 'cancelled' });
      } else {
        onError({ message: result.error.message || 'Payment failed. Please try again.' });
      }
      return;
    }

    if (result.paymentDetails) {
      onSuccess({
        transactionId: result.paymentDetails.paymentMessage || result.paymentDetails.cf_payment_id || 'TXN_' + Date.now(),
        orderId: result.paymentDetails.order_id || '',
        paymentDetails: result.paymentDetails,
      });
    }

  } catch (err) {
    console.error('Cashfree payment error:', err);
    onError({ message: err.message || 'Payment could not be initiated. Please try again.' });
  }
};

export const formatAmount = (amount) => `₹${parseFloat(amount).toFixed(2)}`;

// Keep old name as alias so existing imports don't break
export const initiateOTSPayment = initiateCashfreePayment;
export const initiateSubscriptionPayment = initiateCashfreePayment;
